(() => {
    const scriptElement = document.currentScript;
    const rootId = scriptElement?.dataset.faqRoot || '';
    const designMode = scriptElement?.dataset.designMode === 'true';
    // All FAQ pages: preserve the incoming fragment without a delayed animation.
    // theme.liquid may have stored and removed the fragment before this section loaded.
    const initialUrlHash = window.location.hash;
    const initialFaqId = (() => {
      try { return decodeURIComponent(initialUrlHash.replace(/^#/, '')); }
      catch { return ''; }
    })();
    if (initialFaqId && !window.location.hash) {
      const target = document.getElementById(initialFaqId);
      if (target && target.closest('[data-keyideas-faq]')) {
        window.history.replaceState(window.history.state, '',
          window.location.pathname + window.location.search + '#' + encodeURIComponent(initialFaqId));
      }
    }
    let deferredInitialTargetId = '';
    const restoreDeferredHashWhenSafe = () => {};

    const initFaq = (root) => {
      if (!root || root.dataset.keyideasFaqReady === 'true') return;
      root.dataset.keyideasFaqReady = 'true';

      const search = root.querySelector('[data-faq-search]');
      const searchForm = root.querySelector('[data-faq-search-form]');
      const clearButton = root.querySelector('[data-faq-clear]');
      const printButton = root.querySelector('[data-faq-print]');
      const items = Array.from(root.querySelectorAll('[data-faq-item]'));
      const noResults = root.querySelector('[data-faq-no-results]');
      const layout = root.querySelector('[data-faq-layout]');
      const tocLinks = Array.from(root.querySelectorAll('[data-faq-target]'));
      const mobileToc = root.querySelector('[data-mobile-toc]');
      const mobileToggle = root.querySelector('[data-mobile-toc-toggle]');
      const mobilePanel = root.querySelector('[data-mobile-toc-panel]');
      const mobileCurrent = root.querySelector('[data-mobile-toc-current]');
      let observer;
      const listenerController = new AbortController();
      let resizeFrame;
      let activeFrame;
      let scrollEndTimer;
      let lockedTargetId = '';
      let isInitialHashNavigation = false;
      let initialScrollStyles;
      let initialNavigationCleanup = null;
      let layoutShiftCleanup = null;
      let scrollRestorationBeforeInitial = null;

      const normalize = (value) => value.toLocaleLowerCase().replace(/\s+/g, ' ').trim();

      const highlightSelectors = [
        '.ki-app-faq__item-heading',
        '.ki-app-faq__item-copy',
        '.ki-app-faq__toc-link'
      ];

      const clearHighlights = () => {
        root.querySelectorAll('mark.ki-faq-highlight').forEach((mark) => {
          mark.replaceWith(document.createTextNode(mark.textContent || ''));
        });

        highlightSelectors.forEach((selector) => {
          root.querySelectorAll(selector).forEach((element) => element.normalize());
        });
      };

      const highlightText = (element, query) => {
        if (!element || !query) return;

        const queryLower = query.toLocaleLowerCase();
        const walker = document.createTreeWalker(
          element,
          NodeFilter.SHOW_TEXT,
          {
            acceptNode(node) {
              if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
              if (node.parentElement && node.parentElement.closest('mark.ki-faq-highlight')) {
                return NodeFilter.FILTER_REJECT;
              }
              return NodeFilter.FILTER_ACCEPT;
            }
          }
        );

        const textNodes = [];
        while (walker.nextNode()) textNodes.push(walker.currentNode);

        textNodes.forEach((node) => {
          const value = node.nodeValue;
          const valueLower = value.toLocaleLowerCase();
          let cursor = 0;
          let index = valueLower.indexOf(queryLower);

          if (index === -1) return;

          const fragment = document.createDocumentFragment();

          while (index !== -1) {
            if (index > cursor) {
              fragment.appendChild(document.createTextNode(value.slice(cursor, index)));
            }

            const mark = document.createElement('mark');
            mark.className = 'ki-faq-highlight';
            mark.textContent = value.slice(index, index + query.length);
            fragment.appendChild(mark);

            cursor = index + query.length;
            index = valueLower.indexOf(queryLower, cursor);
          }

          if (cursor < value.length) {
            fragment.appendChild(document.createTextNode(value.slice(cursor)));
          }

          node.replaceWith(fragment);
        });
      };

      const headerSelectors = [
        '.ki-header',
        'sticky-header',
        '.header-wrapper',
        '.shopify-section-group-header-group'
      ];
      let headerCandidates = [];
      let headerOffsetFrame = 0;
      let headerTransitionFrame = 0;
      let headerTransitionUntil = 0;
      let headerObserver;
      let headerResizeObserver;

      const refreshHeaderCandidates = () => {
        headerCandidates = Array.from(new Set(
          headerSelectors.flatMap((selector) => Array.from(document.querySelectorAll(selector)))
        ));
      };

      const measureOffset = () => {
        if (!headerCandidates.length || headerCandidates.some((header) => !header.isConnected)) observeHeaders();
        let bottom = 0;

        headerCandidates.forEach((header) => {
          const rect = header.getBoundingClientRect();
          const position = getComputedStyle(header).position;
          if ((position === 'fixed' || position === 'sticky') && rect.top <= 2 && rect.bottom > 0) {
            bottom = Math.max(bottom, rect.bottom);
          }
        });

        const gap = Math.min(20, Math.max(12, Number.parseInt(root.dataset.headerGap || '16', 10) || 16));
        return Math.max(0, Math.ceil(bottom + gap));
      };

      const setHeaderOffset = () => {
        const offset = `${measureOffset()}px`;
        if (root.style.getPropertyValue('--ki-faq-header-offset') !== offset) {
          root.style.setProperty('--ki-faq-header-offset', offset);
        }
      };

      const scheduleHeaderOffset = () => {
        if (headerOffsetFrame) return;
        headerOffsetFrame = window.requestAnimationFrame(() => {
          headerOffsetFrame = 0;
          setHeaderOffset();
        });
      };

      const followHeaderMotion = () => {
        headerTransitionUntil = performance.now() + 500;
        if (headerTransitionFrame) return;
        const update = () => {
          setHeaderOffset();
          if (performance.now() < headerTransitionUntil) {
            headerTransitionFrame = window.requestAnimationFrame(update);
          } else {
            headerTransitionFrame = 0;
          }
        };
        headerTransitionFrame = window.requestAnimationFrame(update);
      };

      const observeHeaders = () => {
        refreshHeaderCandidates();
        if (headerObserver) headerObserver.disconnect();
        if (headerResizeObserver) headerResizeObserver.disconnect();
        headerObserver = new MutationObserver(followHeaderMotion);
        if (window.ResizeObserver) headerResizeObserver = new ResizeObserver(scheduleHeaderOffset);
        headerCandidates.forEach((header) => {
          headerObserver.observe(header, { attributes: true, attributeFilter: ['class', 'style'] });
          if (headerResizeObserver) headerResizeObserver.observe(header);
        });
      };

      const setActive = (id) => {
        const targetItem = document.getElementById(id);
        if (!targetItem || targetItem.hidden) return;

        tocLinks.forEach((link) => {
          const isActive = link.dataset.faqTarget === id;
          link.classList.toggle('is-active', isActive);
          if (isActive) link.setAttribute('aria-current', 'true');
          else link.removeAttribute('aria-current');
        });

        if (mobileCurrent) {
          mobileCurrent.textContent = targetItem.dataset.faqLabel || '';
        }
      };

      const closeMobileToc = () => {
        if (!mobileToggle || !mobilePanel) return;
        mobileToggle.setAttribute('aria-expanded', 'false');
        mobilePanel.hidden = true;
      };

      const disableInitialSmoothScroll = () => {
        if (initialScrollStyles) return;

        initialScrollStyles = [document.documentElement, document.body].map((element) => ({
          element,
          value: element.style.getPropertyValue('scroll-behavior'),
          priority: element.style.getPropertyPriority('scroll-behavior')
        }));

        initialScrollStyles.forEach(({ element }) => {
          element.style.setProperty('scroll-behavior', 'auto', 'important');
        });
      };

      const restoreInitialScrollState = () => {
        if (initialScrollStyles) {
          initialScrollStyles.forEach(({ element, value, priority }) => {
            if (value) element.style.setProperty('scroll-behavior', value, priority);
            else element.style.removeProperty('scroll-behavior');
          });
          initialScrollStyles = null;
        }

        if ('scrollRestoration' in window.history && scrollRestorationBeforeInitial !== null) {
          window.history.scrollRestoration = scrollRestorationBeforeInitial;
          scrollRestorationBeforeInitial = null;
        }
      };

      const getActivationLine = () => {
        const offset = Number.parseFloat(getComputedStyle(root).getPropertyValue('--ki-faq-header-offset')) || 0;
        return Math.min(Math.max(0, Math.ceil(offset + 2)), Math.max(0, window.innerHeight - 1));
      };

      const getCurrentItem = () => {
        const visibleItems = items.filter((item) => !item.hidden);
        if (!visibleItems.length) return null;

        const activationLine = getActivationLine();
        let current = visibleItems[0];

        visibleItems.forEach((item) => {
          if (item.getBoundingClientRect().top <= activationLine) current = item;
        });

        if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2) {
          current = visibleItems[visibleItems.length - 1];
        }

        return current;
      };

      const updateActiveFromPosition = () => {
        window.cancelAnimationFrame(activeFrame);
        activeFrame = window.requestAnimationFrame(() => {
          if (isInitialHashNavigation || lockedTargetId) {
            if (lockedTargetId) setActive(lockedTargetId);
            return;
          }

          const current = getCurrentItem();
          if (current) setActive(current.id);
        });
      };

      const finishProgrammaticScroll = () => {
        if (isInitialHashNavigation) return;
        lockedTargetId = '';
        updateActiveFromPosition();
      };

      const onScroll = () => {
        scheduleHeaderOffset();
        if (isInitialHashNavigation) {
          if (lockedTargetId) setActive(lockedTargetId);
          return;
        }

        if (lockedTargetId) {
          setActive(lockedTargetId);
          return;
        }

        updateActiveFromPosition();
      };

      const watchVisibleItems = () => {
        if (observer) observer.disconnect();
        if (isInitialHashNavigation) return;

        const visibleItems = items.filter((item) => !item.hidden);
        if (!visibleItems.length) return;

        const activationLine = getActivationLine();
        const bottomInset = Math.max(0, window.innerHeight - activationLine - 1);
        observer = new IntersectionObserver(updateActiveFromPosition, {
          rootMargin: `-${activationLine}px 0px -${bottomInset}px 0px`,
          threshold: 0
        });

        visibleItems.forEach((item) => observer.observe(item));
        updateActiveFromPosition();
      };

      const filterFaq = () => {
        clearHighlights();

        const rawQuery = search ? search.value.trim() : '';
        const query = normalize(rawQuery);
        let matches = 0;

        items.forEach((item) => {
          const match = !query || normalize(item.textContent).includes(query);
          item.hidden = !match;
          if (match) matches += 1;
        });

        // Keep the full Table of Contents whenever at least one FAQ section matches.
        tocLinks.forEach((link) => {
          link.hidden = false;
        });

        const hasNoResults = Boolean(query) && matches === 0;

        if (clearButton) clearButton.hidden = !query;
        if (noResults) noResults.hidden = !hasNoResults;
        if (layout) layout.classList.toggle('is-no-results', hasNoResults);
        if (mobileToc) mobileToc.hidden = hasNoResults;

        if (rawQuery && matches > 0) {
          items
            .filter((item) => !item.hidden)
            .forEach((item) => {
              highlightText(item.querySelector('.ki-app-faq__item-heading'), rawQuery);
              highlightText(item.querySelector('.ki-app-faq__item-copy'), rawQuery);
            });

          tocLinks.forEach((link) => highlightText(link, rawQuery));
        }

        if (hasNoResults) {
          if (observer) observer.disconnect();
          lockedTargetId = '';
          return;
        }

        watchVisibleItems();
      };

      // One controlled animation avoids conflicts between native smooth scrolling,
      // theme scroll-behavior rules and scroll-triggered active-section updates.
      let navigationToken = 0;
      let navigationFrame = 0;

      // Keep the heading anchored while late fonts or lazy media finish laying out.
      // ResizeObserver makes this event-driven; the timeout keeps it strictly bounded.
      const holdTargetPosition = (heading, targetId, token) => {
        if (layoutShiftCleanup) layoutShiftCleanup();

        let observer = null;
        let alignFrame = 0;
        let released = false;
        const intentEvents = ['wheel', 'touchstart', 'pointerdown', 'keydown'];

        const align = () => {
          alignFrame = 0;
          if (released || token !== navigationToken) return;
          const difference = heading.getBoundingClientRect().top - measureOffset();
          if (Math.abs(difference) > 1) {
            window.scrollTo({ top: Math.max(0, window.scrollY + difference), behavior: 'instant' });
          }
          setActive(targetId);
        };

        const scheduleAlign = () => {
          if (!alignFrame && !released && token === navigationToken) {
            alignFrame = window.requestAnimationFrame(align);
          }
        };

        const release = () => {
          if (released) return;
          released = true;
          if (observer) observer.disconnect();
          window.cancelAnimationFrame(alignFrame);
          window.clearTimeout(releaseTimer);
          intentEvents.forEach((eventName) => {
            window.removeEventListener(eventName, release, true);
          });
          if (layoutShiftCleanup === release) layoutShiftCleanup = null;
          if (token === navigationToken) {
            lockedTargetId = '';
            updateActiveFromPosition();
          }
        };

        if ('ResizeObserver' in window) {
          observer = new ResizeObserver(scheduleAlign);
          observer.observe(document.documentElement);
          observer.observe(root);
          observer.observe(heading);
        }

        Array.from(document.images).forEach((image) => {
          const isBeforeTarget = Boolean(image.compareDocumentPosition(heading) & Node.DOCUMENT_POSITION_FOLLOWING);
          if (isBeforeTarget && !image.complete) {
            image.addEventListener('load', scheduleAlign, { once: true });
            image.addEventListener('error', scheduleAlign, { once: true });
          }
        });

        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(scheduleAlign);
        }

        intentEvents.forEach((eventName) => {
          window.addEventListener(eventName, release, { once: true, passive: true, capture: true });
        });

        const releaseTimer = window.setTimeout(release, 1800);
        layoutShiftCleanup = release;
        scheduleAlign();
      };
      const scrollToTarget = (targetId, behavior = 'smooth', onComplete = null, useManualFrames = false) => {
        if (layoutShiftCleanup) layoutShiftCleanup();
        const target = items.find((item) => item.id === targetId);
        if (!target) return false;
        if (target.hidden && search) {
          search.value = '';
          filterFaq();
        }
        if (target.hidden) return false;

        const token = ++navigationToken;
        window.cancelAnimationFrame(navigationFrame);
        window.clearTimeout(scrollEndTimer);
        lockedTargetId = targetId;
        setActive(targetId);
        closeMobileToc();
        setHeaderOffset();

        // Align the actual heading rather than the article's padded border box.
        const heading = target.querySelector('.ki-app-faq__item-heading') || target;
        const targetY = () => Math.max(0, Math.min(
          document.documentElement.scrollHeight - window.innerHeight,
          window.scrollY + heading.getBoundingClientRect().top - measureOffset()
        ));
        const startY = window.scrollY;
        const initialDestination = targetY();
        const distance = Math.abs(initialDestination - startY);
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        // Initial deep links need a perceptible top-to-section journey; ordinary TOC
        // navigation retains the original duration and behavior.
        const duration = behavior === 'auto' || reducedMotion ? 0 : useManualFrames
          ? Math.min(2400, Math.max(1400, distance * 0.65))
          : Math.min(1050, Math.max(420, distance * 0.42));
        const started = performance.now();
        const ease = (t) => 1 - Math.pow(1 - t, 3);
        let previousY = startY;

        const finish = () => {
          if (token !== navigationToken) return;
          // Fonts or lazy-loaded content can shift the target while moving.
          // Correct position without a visible instant jump during animation.
          const finalY = targetY();
          if (Math.abs(finalY - window.scrollY) > 1) window.scrollTo(0, finalY);
          setActive(targetId);
          if (onComplete) onComplete();
          holdTargetPosition(heading, targetId, token);
        };

        // The initial animation uses a FIXED document-space destination. Measuring
        // the destination again from a scrolling viewport can race Chrome's pending
        // fragment/layout state and collapse the perceived animation.
        const fixedInitialDestination = initialDestination;
        let animationStartedAt = null;
        const animate = (now) => {
          if (token !== navigationToken) return;
          if (animationStartedAt === null) animationStartedAt = now;
          const elapsed = useManualFrames ? now - animationStartedAt : now - started;
          const t = duration ? Math.min(1, elapsed / duration) : 1;
          const destination = useManualFrames ? fixedInitialDestination : targetY();
          const desiredY = t >= 1 ? destination : startY + (destination - startY) * ease(t);
          if (useManualFrames) window.scrollTo(0, Math.round(desiredY));
          else window.scrollTo({ top: desiredY, behavior: 'instant' });
          previousY = window.scrollY;
          if (t < 1) navigationFrame = window.requestAnimationFrame(animate);
          else finish();
        };

        if (!duration) {
          if (useManualFrames) window.scrollTo(0, initialDestination);
          else window.scrollTo({ top: initialDestination, behavior: 'instant' });
          finish();
        } else {
          navigationFrame = window.requestAnimationFrame(animate);
        }
        return true;
      };

      const getHashTargetId = () => {
        let targetId = '';

        try {
          targetId = decodeURIComponent(window.location.hash.slice(1));
        } catch {
          return '';
        }

        const target = document.getElementById(targetId);
        return target && root.contains(target) ? targetId : '';
      };

      const positionInitialHashTarget = (targetId) => {
        const target = items.find((item) => item.id === targetId);
        if (!target) return;

        const heading = target.querySelector('.ki-app-faq__item-heading') || target;
        const started = performance.now();
        const maximumWait = 4000;
        const quietPeriod = 220;
        // Do not let the browser's reload/navigation input cancel the new document.
        const intentEvents = [];
        let stableSince = started;
        let previousTop = null;
        let previousHeight = null;
        let frame = 0;
        let observer = null;
        let finished = false;

        const markChanged = () => {
          if (!finished) stableSince = performance.now();
        };

        const cleanup = () => {
          if (finished) return;
          finished = true;
          window.cancelAnimationFrame(frame);
          window.removeEventListener('load', markChanged);
          intentEvents.forEach((eventName) => {
            window.removeEventListener(eventName, cancel, true);
          });
          if (observer) observer.disconnect();
          if (initialNavigationCleanup === cancel) initialNavigationCleanup = null;
        };

        const cancel = () => {
          cleanup();
          isInitialHashNavigation = false;
          lockedTargetId = '';
          restoreInitialScrollState();
          watchVisibleItems();
        };

        const navigate = () => {
          cleanup();
          // Keep scroll observers suppressed until the initial motion is complete.
          // Give the browser two paints at the page top before moving the viewport.
          window.scrollTo(0, 0);
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              window.scrollTo(0, 0);
              const didStart = scrollToTarget(targetId, 'smooth', () => {
                isInitialHashNavigation = false;
                restoreInitialScrollState();
                restoreDeferredHashWhenSafe();
                watchVisibleItems();
              }, true);
              if (!didStart) {
                isInitialHashNavigation = false;
                restoreInitialScrollState();
                restoreDeferredHashWhenSafe();
                watchVisibleItems();
              }
            });
          });
        };

        const check = (now) => {
          if (finished) return;

          // Prevent the browser's later native fragment jump from racing this handler.
          if (window.scrollY !== 0) window.scrollTo(0, 0);

          const currentTop = window.scrollY + heading.getBoundingClientRect().top;
          const currentHeight = document.documentElement.scrollHeight;
          const moved = previousTop !== null && (
            Math.abs(currentTop - previousTop) > 1 ||
            Math.abs(currentHeight - previousHeight) > 1
          );

          if (moved) stableSince = now;
          previousTop = currentTop;
          previousHeight = currentHeight;

          const layoutIsQuiet = now - stableSince >= quietPeriod;
          const pageIsLoaded = document.readyState === 'complete';
          const timedOut = now - started >= maximumWait;

          if ((pageIsLoaded && layoutIsQuiet) || timedOut) {
            navigate();
            return;
          }

          frame = window.requestAnimationFrame(check);
        };

        if ('ResizeObserver' in window) {
          observer = new ResizeObserver(markChanged);
          observer.observe(document.documentElement);
          observer.observe(root);
          observer.observe(heading);
        }

        window.addEventListener('load', markChanged, { once: true });
        intentEvents.forEach((eventName) => {
          window.addEventListener(eventName, cancel, { once: true, passive: true, capture: true });
        });

        Array.from(document.images).forEach((image) => {
          const isBeforeTarget = Boolean(image.compareDocumentPosition(heading) & Node.DOCUMENT_POSITION_FOLLOWING);
          if (isBeforeTarget && !image.complete) {
            image.addEventListener('load', markChanged, { once: true });
            image.addEventListener('error', markChanged, { once: true });
          }
        });

        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(markChanged);
        }

        initialNavigationCleanup = cancel;
        frame = window.requestAnimationFrame(check);
      };

      const handleHashNavigation = () => {
        if (isInitialHashNavigation) return;
        // All FAQ pages: avoid a second animated jump for fragment navigation.
        if (true) {
          const id = getHashTargetId();
          if (id) setActive(id);
          return;
        }

        const targetId = getHashTargetId();
        if (!targetId) {
          lockedTargetId = '';
          updateActiveFromPosition();
          return;
        }

        setHeaderOffset();
        scrollToTarget(targetId, 'smooth');
      };

      tocLinks.forEach((link) => {
        link.addEventListener('click', () => {
          const targetId = link.dataset.faqTarget;
          // Smoothly scroll to the exact clicked FAQ, including on the first click.
          const behavior = 'smooth';

          if (window.location.hash !== `#${targetId}`) {
            window.history.pushState(
              { faqTarget: targetId },
              '',
              `#${targetId}`
            );
          }

          const navigated = scrollToTarget(targetId, behavior);
          if (navigated) closeMobileToc();
        });
      });

      root.addEventListener('shopify:block:select', (event) => {
        const item = event.target.closest('[data-faq-item]');
        if (item && root.contains(item)) scrollToTarget(item.id, 'auto');
      });

      if (searchForm) searchForm.addEventListener('submit', (event) => event.preventDefault());
      if (search) search.addEventListener('input', filterFaq);
      // Use the current window's native print dialog (no hidden iframe).
      const printFaqContent = () => {
        const content = root.querySelector('[data-faq-content]');
        if (!content) return;
        const articles = Array.from(content.querySelectorAll('[data-faq-item]'));
        if (!articles.length) return;

        document.getElementById('ki-faq-print-root')?.remove();
        const printRoot = document.createElement('div');
        printRoot.id = 'ki-faq-print-root';
        printRoot.setAttribute('aria-hidden', 'true');

        articles.forEach((article) => {
          const copy = article.cloneNode(true);
          copy.removeAttribute('hidden');
          copy.querySelectorAll('[hidden]').forEach((el) => el.removeAttribute('hidden'));
          copy.querySelectorAll('mark.ki-faq-highlight').forEach((mark) => {
            mark.replaceWith(document.createTextNode(mark.textContent || ''));
          });
          copy.querySelectorAll('script, iframe, video, button').forEach((el) => el.remove());
          printRoot.appendChild(copy);
        });

        document.body.appendChild(printRoot);
        document.body.classList.add('ki-faq-printing');

        let cleaned = false;
        const cleanup = () => {
          if (cleaned) return;
          cleaned = true;
          document.body.classList.remove('ki-faq-printing');
          printRoot.remove();
          window.removeEventListener('afterprint', cleanup);
        };
        window.addEventListener('afterprint', cleanup);
        try {
          // Synchronous call retains the browser's user-gesture permission.
          window.print();
        } catch (error) {
          cleanup();
          console.error('FAQ print failed:', error);
        }
        // Fallback for browsers that do not emit afterprint.
        setTimeout(cleanup, 60000);
      };
      if (printButton) printButton.addEventListener('click', printFaqContent);
      if (clearButton) {
        clearButton.addEventListener('click', () => {
          search.value = '';
          filterFaq();
          search.focus();
        });
      }

      if (mobileToggle && mobilePanel) {
        mobileToggle.addEventListener('click', () => {
          const expanded = mobileToggle.getAttribute('aria-expanded') === 'true';
          mobileToggle.setAttribute('aria-expanded', String(!expanded));
          mobilePanel.hidden = expanded;
        });

        document.addEventListener('click', (event) => {
          if (!mobileToc.contains(event.target)) closeMobileToc();
        }, { signal: listenerController.signal });

        mobileToc.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            closeMobileToc();
            mobileToggle.focus();
          }
        });
      }

      const onResize = () => {
        window.cancelAnimationFrame(resizeFrame);
        resizeFrame = window.requestAnimationFrame(() => {
          setHeaderOffset();
          watchVisibleItems();
        });
      };

      // Initial hash navigation is handled immediately below for every FAQ page.
      const initialTargetId = '';

      window.addEventListener('resize', onResize, { passive: true, signal: listenerController.signal });
      window.addEventListener('scroll', onScroll, { passive: true, signal: listenerController.signal });
      window.addEventListener('hashchange', handleHashNavigation, { signal: listenerController.signal });
      observeHeaders();
      setHeaderOffset();
      filterFaq();

      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target !== root && !event.target.contains(root)) return;
        listenerController.abort();
        if (headerObserver) headerObserver.disconnect();
        if (headerResizeObserver) headerResizeObserver.disconnect();
        if (observer) observer.disconnect();
        [headerOffsetFrame, headerTransitionFrame, resizeFrame, activeFrame].forEach((frame) => {
          if (frame) window.cancelAnimationFrame(frame);
        });
      }, { signal: listenerController.signal });

      if (initialTargetId) positionInitialHashTarget(initialTargetId);

      // All FAQ pages: keep the deep-linked FAQ at the correct position while
      // late theme scripts, fonts and image layout complete. No animation.
      {
        const nativeTargetId = getHashTargetId() || initialFaqId;
        if (nativeTargetId && !window.location.hash) {
          history.replaceState(history.state, '', location.pathname + location.search + '#' + encodeURIComponent(nativeTargetId));
        }
        const target = nativeTargetId && items.find((item) => item.id === nativeTargetId);
        if (target) {
          setActive(nativeTargetId);
          const heading = target.querySelector('.ki-app-faq__item-heading') || target;
          const previousRestoration = 'scrollRestoration' in history ? history.scrollRestoration : null;
          if (previousRestoration !== null) history.scrollRestoration = 'manual';
          let active = true;
          let frameId = 0;
          const start = performance.now();
          const maxLockMs = 3200;
          const stop = () => {
            if (!active) return;
            active = false;
            cancelAnimationFrame(frameId);
            ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach((name) =>
              window.removeEventListener(name, stop, true));
            if (previousRestoration !== null) history.scrollRestoration = previousRestoration;
          };
          const align = (now) => {
            if (!active) return;
            if (location.hash !== `#${nativeTargetId}` || now - start > maxLockMs) {
              stop();
              return;
            }
            const y = Math.max(0, Math.min(
              document.documentElement.scrollHeight - innerHeight,
              scrollY + heading.getBoundingClientRect().top - measureOffset()
            ));
            if (Math.abs(scrollY - y) > 2) window.scrollTo({ top: y, behavior: 'instant' });
            frameId = requestAnimationFrame(align);
          };
          ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach((name) =>
            window.addEventListener(name, stop, { capture: true, passive: true, once: true }));
          // Immediate positioning avoids a visible pause at the top.
          align(performance.now());
          window.addEventListener('pagehide', stop, { once: true });
        }
      }
    };

    // Keep FAQ HTML and JSON-LD server-rendered. Defer only interaction JS.
    // Deep links and Shopify Theme Editor need immediate initialization.
    const faqRoot = document.getElementById(rootId);
    const hasFaqDeepLink = (() => {
      try {
        const id = decodeURIComponent(location.hash.slice(1));
        return !!(id && faqRoot && faqRoot.querySelectorAll('[data-faq-item]').length &&
          Array.from(faqRoot.querySelectorAll('[data-faq-item]')).some(item => item.id === id));
      } catch { return false; }
    })();
    const initialize = () => initFaq(faqRoot);
    if (hasFaqDeepLink || designMode) {
      initialize();
    } else {
      const afterLoad = () => {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(initialize, { timeout: 1500 });
        } else {
          window.setTimeout(initialize, 100);
        }
      };
      if (document.readyState === 'complete') afterLoad();
      else window.addEventListener('load', afterLoad, { once: true });
      // Ensure search/print/TOC works even if someone interacts before load.
      if (faqRoot) {
        const activateOnInteraction = () => initialize();
        faqRoot.addEventListener('pointerdown', activateOnInteraction, { once: true, capture: true });
        faqRoot.addEventListener('focusin', activateOnInteraction, { once: true, capture: true });
      }
    }

    const onSectionLoad = (event) => {
      const root = event.target.querySelector(`#${rootId}`) ||
        (event.target.id === rootId ? event.target : null);
      if (root) initFaq(root);
    };
    const onSectionUnload = (event) => {
      if (event.target.id !== 'shopify-section-' + rootId.replace(/^keyideas-faq-/, '') &&
          event.target.id !== rootId && !event.target.querySelector(`#${rootId}`)) return;
      document.removeEventListener('shopify:section:load', onSectionLoad);
      document.removeEventListener('shopify:section:unload', onSectionUnload);
    };
    document.addEventListener('shopify:section:load', onSectionLoad);
    document.addEventListener('shopify:section:unload', onSectionUnload);
  })();
